import type { Cancellable } from '../../types';
import type { FunctionComponent, PropsWithChildren } from 'react';

import { Flex } from '@rango-dev/whale';
import { ToastProvider } from '@rango-dev/whale/lab';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

import { RANGO_CONTAINER_ID } from '../../constants/theme';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { useIsDesktopNavigation } from '../../hooks/useIsDesktopNavigation';
import { useScrollOnNavigate } from '../../hooks/useScrollOnNavigate';
import { fetchProfileSummary } from '../../state/ProfileSlice';
import { ConnectWalletButton } from '../ConnectWalletButton/ConnectWalletButton';
import { Logo } from '../Logo';
import { MobileMenu } from '../MobileMenu';
import { NavLinks, useNavLinks } from '../NavLinks';
import { ProfileStackModal } from '../newModal';
import { Notifications } from '../Notifications';
import { Settings } from '../Settings';
import { WhaleThemeProvider } from '../WhaleThemeProvider';

import styles from './Layout.module.css';

export const Layout: FunctionComponent<PropsWithChildren> = (props) => {
  const dispatch = useAppDispatch();
  const isDarkMode = useAppSelector((state) => state.settings.isDarkMode);
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const username = useAppSelector((state) => state.profile.username);
  const bodyRef = useRef<HTMLDivElement>(null);
  const fetchProfileSummaryPromise = useRef<Cancellable | null>(null);
  const isDesktopNavigation = useIsDesktopNavigation();
  const { primaryLinks, moreGroups } = useNavLinks();

  useScrollOnNavigate(bodyRef, [/^(\/.*)?\/bridge.*$/]);

  useEffect(() => {
    if (isLoggedIn && username) {
      fetchProfileSummaryPromise.current = dispatch(
        fetchProfileSummary({ username, public: false }),
      );
    }

    return () => fetchProfileSummaryPromise.current?.abort();
  }, [isLoggedIn, username]);

  return (
    /*
     * The whole layout lives inside one WhaleThemeProvider, so the Whale/Radix
     * tokens used by the CSS modules (`--gray-2`, `--accent-10`, ...) resolve with
     * the right appearance everywhere, and overlays portalled into the container
     * (nav popovers, the profile stack, toasts) inherit the same theme.
     */
    <WhaleThemeProvider>
      {/*
       * ToastProvider must be a React descendant of WhaleThemeProvider (not
       * merely portalled into its DOM) — its Viewport re-applies the current
       * Theme from React context, so it needs one above it to inherit.
       */}
      <ToastProvider>
        <div
          id={RANGO_CONTAINER_ID}
          className={clsx(styles.layout, isDarkMode && styles.layoutDark)}>
          <div ref={bodyRef} className={styles.body}>
            <Flex asChild align="center" justify="between" gap="4" px="5" py="4" flexShrink="0">
              <header>
                <Flex align="center" gap="6">
                  <Logo />
                  {isDesktopNavigation && <NavLinks links={primaryLinks} moreGroups={moreGroups} />}
                </Flex>
                <Flex align="center" gap="3">
                  <Settings />
                  {isDesktopNavigation && <Notifications />}
                  <ConnectWalletButton />
                </Flex>
              </header>
            </Flex>
            <Flex
              asChild
              direction="column"
              flexGrow="1"
              width="100%"
              px={{ initial: '4', md: '6' }}
              pt={'4'}
              pb="6">
              <main>{props.children}</main>
            </Flex>
          </div>
          {!isDesktopNavigation && <MobileMenu links={primaryLinks} moreGroups={moreGroups} />}
        </div>
        {/*
         * Rendered inside the same WhaleThemeProvider as the layout so the stack
         * — and any nested Radix Themes overlays its panels render (e.g. the
         * social-wallet intro Dialog) — inherit the app Theme from React context
         * as a NON-root theme, instead of falling back to a Radix default root
         * theme (which would inject a full-height `min-height:100vh; z-index:0`
         * wrapper and hide the page body when the stack opens).
         */}
        <ProfileStackModal />
      </ToastProvider>
    </WhaleThemeProvider>
  );
};
